import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { frasiProva, titoloProva, eRecupero } from './campagnaProva';
import { frasiOrigine, titoloOrigine, daElencoTim } from './coperturaOrigine';
import { frasiProvenienza } from './provenienzaRecord';
import { avviaBulk } from './BulkProgress';
import { mediaUrl, useAuth } from '../auth';
import { IconList, IconTrash, IconSpinner, IconWarning, IconSearch, IconRefresh, IconFilter, IconTools, IconSave, IconSignal, IconCheckCircle, IconGlobe, IconPin, IconBuilding, IconCheck, IconImage, IconPhone, IconClose } from './Icons';
import AudioPlayer from './AudioPlayer';
import SocialLinks from './SocialLinks';
import IndagaAnim from './IndagaAnim';
import VodafoneBadge from './VodafoneBadge';
import { useSingleNotif } from './SingleNotif';
import { useCopertura } from '../coperturaState';
import VfProgress from './VfProgress';
import LucchettoConsistenze from './LucchettoConsistenze';

const PER_PAGE = 50;

// Formatta una velocità in Mbps come "10 Giga" / "2.5 Giga" / "200 Mega".
const fmtVel = (mbps) => {
  if (!mbps) return null;
  return mbps >= 1000 ? `${+(mbps / 1000).toFixed(1)} Giga` : `${mbps} Mega`;
};

// Colore del "lucchetto" SSMATE (stato presidio commerciale TIM).
const LUCCHETTO_HEX = {
  GRIGIO: 'var(--faint)', VERDE: 'var(--ok)', NERO: 'var(--text)', VIOLA: 'var(--info)',
  ROSSO: 'var(--err)', GIALLO: 'var(--warn)', ARANCIONE: 'var(--warn)', 'NON TROVATO': 'var(--line)',
};
const LucchettoBadge = ({ value }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 10, height: 10, borderRadius: '50%', background: LUCCHETTO_HEX[value] || 'var(--faint)',
      border: value === 'NON TROVATO' ? '1px solid var(--faint)' : 'none', display: 'inline-block' }} />
    {value}
  </span>
);

// Codice Fiscale valido per SSMATE: 11 cifre (CF azienda = P.IVA) o 16 alfanumerici (persona).
const isCfValido = (cf) => {
  const v = (cf || '').trim().toUpperCase();
  return /^[0-9]{11}$/.test(v) || /^[A-Z0-9]{16}$/.test(v);
};

// Consistenze SSMATE: voci di linee/servizi per il totale nella scheda.
const SS_CONS_KEYS = ['n_linee_fonia', 'n_linee_dati', 'n_offerte_tim_comunica', 'n_sedi',
  'n_linee_ricaricabili_human', 'n_linee_abbonamento_human', 'n_linee_m2m', 'n_linee_m2m_applicative', 'n_servizi_ict'];
const ssConsTotale = (det) => SS_CONS_KEYS.reduce((s, k) => s + (parseInt(det?.[k], 10) || 0), 0);

// Colori semantici dell'esito 456 (coerenti col resto dell'app)
const RES_COLOR = {
  'TIM': { bg: 'rgba(225,29,72,.12)', fg: 'var(--err)' },
  'NON TIM': { bg: 'rgba(22,163,74,.12)', fg: 'var(--ok)' },
  'ERRORE': { bg: 'rgba(239,68,68,.12)', fg: 'var(--err)' },
  'SERVIZIO_NON_DISPONIBILE': { bg: 'rgba(180,83,9,.12)', fg: 'var(--warn)' },
};
const resColor = (r) => RES_COLOR[r] || { bg: 'var(--line)', fg: 'var(--muted)' };

// mini-statistica (es. copertura) e coppia chiave/valore per le schede del dettaglio
const Stat = ({ b, l, color }) => (
  <div>
    <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1, color: color || 'var(--text)' }}>{b}</div>
    <div style={{ fontSize: 10, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 4 }}>{l}</div>
  </div>
);
const KV = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: '6px 10px', fontSize: 12, alignItems: 'center' }}>{children}</div>
);


export default function NumeriTable({ refreshKey, onJobStarted, onDeleted, onVerifiedSingle, disabled }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  // Arrivo da un collegamento (?q=...): chi apre "il record in archivio" dal dettaglio di
  // una riga di campagna deve vedere QUEL record, non l'elenco filtrato come l'aveva
  // lasciato l'ultima volta. Quindi il q dell'indirizzo vince e i filtri salvati si
  // ignorano PER QUESTA VISTA: uno di loro (es. "solo TIM") nasconderebbe proprio il
  // record che il collegamento prometteva di aprire. Ma non si TOCCANO: la persistenza
  // qui sotto si ferma (daLink), cosi' la configurazione dell'utente sulla pagina piu'
  // usata resta dov'era e la ritrova appena naviga normalmente.
  const DA_LINK = (() => { try { return new URLSearchParams(window.location.search).get('q') || ''; } catch { return ''; } })();
  const daLink = useRef(!!DA_LINK);
  // l'indirizzo si ripulisce subito: un F5 (o un ritorno col tasto indietro) non deve
  // ri-applicare il filtro del collegamento come se fosse una scelta dell'utente
  useEffect(() => {
    if (!daLink.current) return;
    try { window.history.replaceState({}, '', '/numeri'); } catch { /* niente history */ }
  }, []);
  // filtri persistenti: ripristinati cambiando pagina/sezione (salvati in localStorage)
  const SAVED = (() => {
    if (DA_LINK) return {};
    try { return JSON.parse(localStorage.getItem('numeri_filtri') || '{}'); } catch { return {}; }
  })();
  const [filter, setFilter] = useState(SAVED.filter || '');
  const [q, setQ] = useState(DA_LINK || SAVED.q || '');
  const [regioneFilter, setRegioneFilter] = useState(SAVED.regioneFilter || '');
  const [provinciaFilter, setProvinciaFilter] = useState(SAVED.provinciaFilter || '');
  const [tipoImpresaFilter, setTipoImpresaFilter] = useState(SAVED.tipoImpresaFilter || '');
  const [lavorazioneFilter, setLavorazioneFilter] = useState(SAVED.lavorazioneFilter || '');
  const [scadutiFilter, setScadutiFilter] = useState(SAVED.scadutiFilter || '');
  const [leadFilter, setLeadFilter] = useState(!!SAVED.leadFilter);
  const [soloDaRivedere, setSoloDaRivedere] = useState(!!SAVED.soloDaRivedere);
  const [scadutiCount, setScadutiCount] = useState(0);
  const [savedLists, setSavedLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [listsOpen, setListsOpen] = useState(false);
  const [regioni, setRegioni] = useState([]);
  const [province, setProvince] = useState([]);
  const [verifyingSingle, setVerifyingSingle] = useState(null);
  const { start: notifStart, done: notifDone } = useSingleNotif();   // notifiche verifica singola (globali)
  const [loading, setLoading] = useState(false);
  const [timingDays, setTimingDays] = useState(180);
  const [copTiming, setCopTiming] = useState(180);
  const [ssTiming, setSsTiming] = useState(90);
  const [ssTimingEnabled, setSsTimingEnabled] = useState(true);
  const [geaTiming, setGeaTiming] = useState(180);
  const [geaBusyId, setGeaBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [sortBy, setSortBy] = useState(SAVED.sortBy || 'imported_at');
  const [sortDir, setSortDir] = useState(SAVED.sortDir || 'desc');
  const [detailNum, setDetailNum] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [copBusyId, setCopBusyId] = useState(null);
  const [ssmateBusyId, setSsmateBusyId] = useState(null);
  const [vfJobId, setVfJobId] = useState(null);
  const [ssShotOpen, setSsShotOpen] = useState(false);   // overlay screenshot SSMATE
  const [ssConsOpen, setSsConsOpen] = useState(false);   // popover dettaglio consistenza
  const [audioOpen, setAudioOpen] = useState(false);     // player registrazione 456 (toggle da icona)
  const [ssRecapNum, setSsRecapNum] = useState(null);    // riepilogo consistenze da lista
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delText, setDelText] = useState('');
  const [coperturaFilter, setCoperturaFilter] = useState(SAVED.coperturaFilter || '');
  const [coperturaEsito, setCoperturaEsito] = useState(SAVED.coperturaEsito || '');
  const [coperturaNew, setCoperturaNew] = useState(!!SAVED.coperturaNew);
  // Contrassegno monouso: i record che il 456 aveva deciso con un numero non loro
  // (ordine delle varianti GNR, corretto). Serve a isolarli e ri-verificarli in blocco.
  const [bug456, setBug456] = useState(!!SAVED.bug456);
  // Contrassegno monouso: i record NON CENSITO perche' la via era scritta abbreviata
  // ("G. MAZZINI" invece di "GIUSEPPE MAZZINI"). Si svuota da solo al nuovo esito copertura.
  const [bugCopertura, setBugCopertura] = useState(!!SAVED.bugCopertura);
  const [ftthVicino, setFtthVicino] = useState(!!SAVED.ftthVicino);
  // "non lavorati" per agente
  const [daFare456, setDaFare456] = useState(!!SAVED.daFare456);
  const [daFareCop, setDaFareCop] = useState(!!SAVED.daFareCop);
  const [daFareSsm, setDaFareSsm] = useState(!!SAVED.daFareSsm);
  const [daFareGea, setDaFareGea] = useState(!!SAVED.daFareGea);
  // esiti agenti SSMATE / Vodafone
  const [ssmateLucchetto, setSsmateLucchetto] = useState(SAVED.ssmateLucchetto || '');
  const [geaVodafone, setGeaVodafone] = useState(SAVED.geaVodafone || '');
  const [exportStato, setExportStato] = useState(SAVED.exportStato || '');
  const [sourceFilter, setSourceFilter] = useState(SAVED.sourceFilter || '');
  const [fonteFilter, setFonteFilter] = useState(SAVED.fonteFilter || '');
  const [indirizzoStato, setIndirizzoStato] = useState(SAVED.indirizzoStato || '');
  const [timInattivo, setTimInattivo] = useState(!!SAVED.timInattivo);
  const [sources, setSources] = useState([]);
  const [filtriOpen, setFiltriOpen] = useState(false);
  const [filtroTab, setFiltroTab] = useState('generale');
  const { abilitata: coperturaAbilitata } = useCopertura();
  const { can, user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';
  const mostraCopertura = coperturaAbilitata && can('agente_copertura');
  // agente Indaga nel dettaglio (solo admin, su indirizzo incompleto o social mancanti)
  const [indagaOpen, setIndagaOpen] = useState(false);
  const [indagaBusy, setIndagaBusy] = useState(false);
  const [indagaData, setIndagaData] = useState(null);
  const [indagaApplied, setIndagaApplied] = useState(false);
  const [indagaSel, setIndagaSel] = useState({}); // { chiave: bool } spunte riepilogo
  const [indagaAgenti, setIndagaAgenti] = useState(null); // {num456, copertura} dopo l'apply

  // Load only regioni/province that HAVE records (with counts) + timing setting
  useEffect(() => {
    axios.get(`${API}/dashboard/aree-counts`)
      .then(r => { setRegioni(r.data?.regioni || []); setProvince(r.data?.province || []); })
      .catch(() => { setRegioni([]); setProvince([]); });
    axios.get(`${API}/settings`)
      .then(r => {
        const d = Number(r.data?.timing_riverifica_giorni); if (d > 0) setTimingDays(d);
        const c = Number(r.data?.copertura_timing_giorni); if (c > 0) setCopTiming(c);
        const g = Number(r.data?.gea_timing_giorni); if (g > 0) setGeaTiming(g);
      })
      .catch(() => {});
    if (can('ssmate')) {
      axios.get(`${API}/ssmate/config`)
        .then(r => {
          const s = Number(r.data?.timing_giorni); if (s > 0) setSsTiming(s);
          setSsTimingEnabled(r.data?.timing_enabled !== false);
        })
        .catch(() => {});
    }
  }, []);  // eslint-disable-line

  useEffect(() => { axios.get(`${API}/numeri/sources`).then((r) => setSources(r.data?.sources || [])).catch(() => {}); }, []);

  // conteggio "da ri-verificare" (timing scaduto), si aggiorna ai refresh
  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setScadutiCount(r.data?.scaduti || 0)).catch(() => {});
  }, [refreshKey]);

  // Saved filter lists
  const loadLists = useCallback(() => {
    axios.get(`${API}/liste-numeri`).then(r => setSavedLists(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);

  const saveList = async () => {
    const nome = window.prompt('Nome della lista (salva i filtri attuali):');
    if (!nome || !nome.trim()) return;
    const filtri = { filter, q, regione: regioneFilter, provincia: provinciaFilter, tipo_impresa: tipoImpresaFilter, lavorazione: lavorazioneFilter, bug_456: bug456, bug_copertura: bugCopertura, ftth_vicino: ftthVicino };
    try {
      await axios.post(`${API}/liste-numeri`, { nome: nome.trim(), filtri });
      toast.success('Lista salvata');
      loadLists();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio lista'); }
  };

  const applyList = (id) => {
    setSelectedListId(id);
    if (!id) return;
    const l = savedLists.find(x => x.id === id);
    const f = (l && l.filtri) || {};
    setFilter(f.filter || ''); setQ(f.q || ''); setRegioneFilter(f.regione || '');
    setProvinciaFilter(f.provincia || ''); setTipoImpresaFilter(f.tipo_impresa || '');
    setLavorazioneFilter(f.lavorazione || '');
    setBug456(!!f.bug_456);
    setBugCopertura(!!f.bug_copertura);
    setFtthVicino(!!f.ftth_vicino);
  };

  const deleteList = async (id) => {
    if (!id) return;
    if (!window.confirm('Eliminare questa lista salvata?')) return;
    try {
      await axios.delete(`${API}/liste-numeri/${id}`);
      if (selectedListId === id) setSelectedListId('');
      loadLists(); toast.success('Lista eliminata');
    } catch { toast.error('Errore eliminazione lista'); }
  };

  // Whether a number can be re-verified now (timing elapsed since last verification).
  // Pending / errors / unavailable can always be (re)verified.
  const canVerify = useCallback((n) => {
    if (!n.verified_at) return true;
    if (n.risultato !== 'TIM' && n.risultato !== 'NON TIM') return true;
    const elapsedDays = (Date.now() - new Date(n.verified_at).getTime()) / 86400000;
    return elapsedDays >= timingDays;
  }, [timingDays]);

  const nextVerifyDate = useCallback((n) => {
    if (!n.verified_at) return null;
    return new Date(new Date(n.verified_at).getTime() + timingDays * 86400000);
  }, [timingDays]);

  // numero già verificato (TIM/NON TIM) il cui timing è scaduto -> da ri-verificare
  const isScaduto = useCallback((n) => {
    if (!n.verified_at || (n.risultato !== 'TIM' && n.risultato !== 'NON TIM')) return false;
    return (Date.now() - new Date(n.verified_at).getTime()) / 86400000 >= timingDays;
  }, [timingDays]);

  const _scadutoGiorni = (dateIso, giorni) => !!dateIso && (Date.now() - new Date(dateIso).getTime()) / 86400000 >= giorni;
  // copertura/SSMATE da fare = mai verificato OPPURE timing scaduto
  const coperturaDaFare = useCallback((n) => !n.copertura_esito || _scadutoGiorni(n.copertura_at, copTiming), [copTiming]);
  const ssmateDaFare = useCallback((n) => !n.ssmate_lucchetto || (ssTimingEnabled && _scadutoGiorni(n.ssmate_verificato_at, ssTiming)), [ssTiming, ssTimingEnabled]);
  // GEA (Vodafone): NON TIM fissi + TIM inattivi (numerazione inattiva), mai fatto / ERRORE / oltre il timing
  const geaDaFare = useCallback((n) => ((n.risultato === 'NON TIM' || (n.risultato === 'TIM' && n.tim456_numerazione_inattiva)) && (n.numero_pulito || '').startsWith('0'))
    && (!n.gea_risultato || n.gea_risultato === 'ERRORE' || _scadutoGiorni(n.gea_data_verifica, geaTiming)), [geaTiming]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
      if (filter) params.set('risultato', filter);
      if (q.trim()) params.set('q', q.trim());
      if (provinciaFilter) params.set('provincia', provinciaFilter);
      else if (regioneFilter) params.set('regione', regioneFilter);
      if (tipoImpresaFilter) params.set('tipo_impresa', tipoImpresaFilter);
      if (lavorazioneFilter) params.set('lavorazione', lavorazioneFilter);
      if (scadutiFilter === 'si') params.set('scaduti', 'true');
      if (leadFilter) params.set('lead', 'true');
      if (soloDaRivedere) params.set('da_rivedere', 'true');
      if (coperturaNew) params.set('copertura_new', 'true');
      if (bug456) params.set('bug_456', 'true');
      if (bugCopertura) params.set('bug_copertura', 'true');
      if (ftthVicino) params.set('ftth_vicino', 'true');
      if (coperturaEsito) params.set('copertura_esito', coperturaEsito);
      if (coperturaFilter) {
        const [tec, down] = coperturaFilter.split(':');
        params.set('copertura_tecnologia', tec);
        if (down === '10000') params.set('copertura_down_min', '10000');
        else if (down === '2500') { params.set('copertura_down_min', '2500'); params.set('copertura_down_max', '10000'); }
        else if (down === '1000') { params.set('copertura_down_min', '1000'); params.set('copertura_down_max', '2500'); }
      }
      if (daFare456) params.set('da_fare_456', 'true');
      if (daFareCop) params.set('da_fare_copertura', 'true');
      if (daFareSsm) params.set('da_fare_ssmate', 'true');
      if (daFareGea) params.set('da_fare_gea', 'true');
      if (exportStato) params.set('export_stato', exportStato);
      if (sourceFilter) params.set('source', sourceFilter);
      if (fonteFilter) params.set('fonte', fonteFilter);
      if (indirizzoStato) params.set('indirizzo_stato', indirizzoStato);
      if (timInattivo) params.set('tim_inattivo', 'true');
      if (ssmateLucchetto) params.set('ssmate_lucchetto', ssmateLucchetto);
      if (geaVodafone) params.set('gea_vodafone', geaVodafone);
      params.set('sort', sortBy);
      params.set('order', sortDir);
      const r = await axios.get(`${API}/numeri?${params}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 0);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [page, filter, q, provinciaFilter, regioneFilter, tipoImpresaFilter, lavorazioneFilter, scadutiFilter, leadFilter, soloDaRivedere, coperturaEsito, coperturaFilter, coperturaNew, bug456, bugCopertura, ftthVicino, daFare456, daFareCop, daFareSsm, daFareGea, exportStato, sourceFilter, fonteFilter, indirizzoStato, timInattivo, ssmateLucchetto, geaVodafone, sortBy, sortDir]);

  useEffect(() => { load(); }, [load, refreshKey]);
  // reset dei pannelli SSMATE quando cambia il numero in dettaglio
  useEffect(() => { setSsShotOpen(false); setSsConsOpen(false); setAudioOpen(false); }, [detailNum?.id]);
  // apertura dettaglio = "visto": azzera il badge NEW (FTTH)
  useEffect(() => {
    if (detailNum?.copertura_new) {
      const id = detailNum.id;
      axios.post(`${API}/copertura/${id}/seen`).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, copertura_new: false } : x)));
    }
  }, [detailNum?.id]);  // eslint-disable-line
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    setSelectAllFiltered(false);
  }, [filter, q, provinciaFilter, regioneFilter, tipoImpresaFilter, lavorazioneFilter, scadutiFilter, leadFilter, soloDaRivedere, coperturaEsito, coperturaFilter, coperturaNew, bug456, bugCopertura, ftthVicino, daFare456, daFareCop, daFareSsm, daFareGea, exportStato, sourceFilter, fonteFilter, indirizzoStato, timInattivo, ssmateLucchetto, geaVodafone, sortBy, sortDir]);

  // persisti i filtri (così cambiando pagina/sezione e tornando restano)
  useEffect(() => {
    // visita nata da un collegamento: i filtri a schermo sono quelli del link, non una
    // scelta dell'utente, e salvarli gli riscriverebbe la sua configurazione
    if (daLink.current) return;
    try {
      localStorage.setItem('numeri_filtri', JSON.stringify({
        filter, q, regioneFilter, provinciaFilter, tipoImpresaFilter, lavorazioneFilter,
        scadutiFilter, leadFilter, soloDaRivedere, coperturaFilter, coperturaEsito, coperturaNew, bug456, bugCopertura, ftthVicino,
        daFare456, daFareCop, daFareSsm, daFareGea, exportStato, sourceFilter, fonteFilter, indirizzoStato, timInattivo, ssmateLucchetto, geaVodafone, sortBy, sortDir,
      }));
    } catch { /* ignora */ }
  }, [filter, q, regioneFilter, provinciaFilter, tipoImpresaFilter, lavorazioneFilter, scadutiFilter, leadFilter, soloDaRivedere, coperturaFilter, coperturaEsito, coperturaNew, bug456, bugCopertura, ftthVicino, daFare456, daFareCop, daFareSsm, daFareGea, exportStato, sourceFilter, fonteFilter, indirizzoStato, timInattivo, ssmateLucchetto, geaVodafone, sortBy, sortDir]);

  // azzera tutti i filtri
  const clearAll = () => {
    setFilter(''); setRegioneFilter(''); setProvinciaFilter(''); setTipoImpresaFilter(''); setQ('');
    setLavorazioneFilter(''); setScadutiFilter(''); setLeadFilter(false); setSoloDaRivedere(false);
    setCoperturaFilter(''); setCoperturaEsito(''); setCoperturaNew(false); setBug456(false); setBugCopertura(false);
    setDaFare456(false); setDaFareCop(false); setDaFareSsm(false); setDaFareGea(false);
    setExportStato(''); setSourceFilter(''); setFonteFilter(''); setIndirizzoStato(''); setTimInattivo(false);
    setSsmateLucchetto(''); setGeaVodafone('');
  };
  const activeFiltri = [
    filter, regioneFilter, provinciaFilter, tipoImpresaFilter, lavorazioneFilter, scadutiFilter,
    leadFilter, soloDaRivedere, coperturaFilter, coperturaEsito, coperturaNew, bug456, bugCopertura, ftthVicino,
    daFare456, daFareCop, daFareSsm, daFareGea, exportStato, sourceFilter, fonteFilter, indirizzoStato, timInattivo,
    ssmateLucchetto, geaVodafone,
  ].filter(Boolean).length;

  const rowLabel = (id) => {
    const n = items.find(x => x.id === id) || (detailNum && detailNum.id === id ? detailNum : null) || {};
    return n.ragione_sociale || n.numero_pulito || id;
  };

  const handleVerifySingle = async (numero_id, numero_pulito) => {
    setVerifyingSingle(numero_id);
    const nid = notifStart('456', numero_pulito || rowLabel(numero_id));
    try {
      const r = await axios.post(`${API}/verify-single`, { numero_id });
      const ris = r.data.risultato;
      if (ris === 'TIM') notifDone(nid, `TIM (Telecom) · ${r.data.numero_verificato || ''}`, 'var(--ok)');
      else if (ris === 'NON TIM') notifDone(nid, `NON TIM · ${r.data.numero_verificato || ''}`, 'var(--info)');
      else if (ris === 'SERVIZIO_NON_DISPONIBILE') notifDone(nid, 'Servizio 456 non disponibile', 'var(--warn)');
      else notifDone(nid, `Errore: ${r.data.errore || 'nessun risultato'}`, 'var(--err)');
      onVerifiedSingle?.();
      load();
    } catch (e) {
      notifDone(nid, e.response?.data?.detail || 'Errore verifica', 'var(--err)');
    }
    setVerifyingSingle(null);
  };

  const handleVerifyCopertura = async (id) => {
    setCopBusyId(id);
    const nid = notifStart('copertura', rowLabel(id));
    try {
      const r = await axios.post(`${API}/numeri/${id}/copertura`);
      setDetailNum(prev => (prev && prev.id === id ? { ...prev, ...r.data } : prev));
      const e = r.data.copertura_esito;
      if (e === 'COPERTO') notifDone(nid, `Copertura: ${r.data.copertura_tecnologia || 'disponibile'}`, 'var(--ok)');
      else if (e === 'NON CENSITO') notifDone(nid, 'Indirizzo non censito da TIM', 'var(--warn)');
      else if (e === 'NON COPERTO') notifDone(nid, 'Nessuna copertura TIM', 'var(--muted)');
      else notifDone(nid, 'Verifica copertura non riuscita', 'var(--err)');
      onVerifiedSingle?.();
      load();
    } catch (err) {
      notifDone(nid, err.response?.data?.detail || 'Errore verifica copertura', 'var(--err)');
    }
    setCopBusyId(null);
  };

  const verificaSsmate = async (id) => {
    setSsmateBusyId(id);
    const nid = notifStart('ssmate', rowLabel(id));
    try {
      const r = await axios.post(`${API}/ssmate/verifica/${id}`);
      if (r.data.success) {
        setDetailNum(prev => (prev && prev.id === id ? {
          ...prev,
          ssmate_lucchetto: r.data.lucchetto,
          ssmate_consistenza: r.data.consistenza,
          ssmate_lavorabile: r.data.lavorabile,
          ssmate_presidio: r.data.presidio,
          ssmate_verificato_at: r.data.verificato_at,
        } : prev));
        notifDone(nid, `${r.data.lucchetto}${r.data.lavorabile ? ' · lavorabile' : ' · non lavorabile'}`,
          r.data.lavorabile ? 'var(--ok)' : 'var(--err)');
        onVerifiedSingle?.();
        load();
      } else if (r.data.quota_exhausted || r.data.quota_exceeded) {
        const msg = `Crediti SSMATE esauriti — si azzerano alle ${r.data.reset || '02:00'}`;
        notifDone(nid, msg, 'var(--warn)'); toast.warning(msg);
      } else if (r.data.queued) {
        notifDone(nid, 'In coda — retry automatico', 'var(--warn)');
      } else {
        notifDone(nid, r.data.message || 'Non riuscita', 'var(--err)');
      }
    } catch (err) {
      notifDone(nid, err.response?.data?.detail || 'Errore verifica SSMATE', 'var(--err)');
    }
    setSsmateBusyId(null);
  };

  const verificaGea = async (id) => {
    setGeaBusyId(id);
    const nid = notifStart('gea', rowLabel(id));
    try {
      const r = await axios.post(`${API}/gea/verifica/${id}`);
      if (r.data.skipped) {
        notifDone(nid, r.data.motivo || 'Saltato', 'var(--warn)');
      } else if (r.data.disservizio) {
        notifDone(nid, 'Portale Vodafone in disservizio — riprova', 'var(--warn)');
      } else {
        const ris = r.data.risultato;
        setDetailNum(prev => (prev && prev.id === id ? { ...prev, gea_risultato: ris, gea_numero: r.data.numero_verificato, gea_tipo: r.data.gea_tipo, gestore_rete_fissa: r.data.gestore_rete_fissa } : prev));
        notifDone(nid, ris === 'VODAFONE' ? `Vodafone (${r.data.gea_tipo === 'NUOVO' ? 'nuovo' : 'confermato'})` : (ris === 'NEGATIVO' ? 'Non Vodafone' : ris),
          ris === 'VODAFONE' ? 'var(--err)' : 'var(--muted)');
        onVerifiedSingle?.();
        load();
      }
    } catch (err) {
      notifDone(nid, err.response?.data?.detail || 'Errore verifica Vodafone', 'var(--err)');
    }
    setGeaBusyId(null);
  };

  const avviaIndaga = async () => {
    if (!detailNum) return;
    setIndagaOpen(true); setIndagaBusy(true); setIndagaData(null); setIndagaApplied(false); setIndagaSel({}); setIndagaAgenti(null);
    try {
      const r = await axios.post(`${API}/indaga`, {
        numero: detailNum.numero_pulito || detailNum.numero_raw, numero_id: detailNum.id,
      });
      setIndagaData(r.data);
      const sel = {};
      (r.data.proposte || []).forEach((p) => { sel[p.chiave] = !!p.default_on; });
      setIndagaSel(sel);
    } catch (e) {
      setIndagaData({ trovato: false, errore: e.response?.data?.detail || 'Errore Indaga' });
    }
    setIndagaBusy(false);
  };
  const applicaIndaga = async () => {
    if (!detailNum || !indagaData) return;
    const campi = (indagaData.proposte || []).filter((p) => indagaSel[p.chiave]).map((p) => p.chiave);
    if (!campi.length) return;
    const numeroPrima = detailNum.numero_pulito;
    const statoPrima = detailNum.copertura_indirizzo_stato;
    setIndagaBusy(true);
    try {
      const r = await axios.post(`${API}/indaga/applica`, { numero_id: detailNum.id, dati: indagaData, campi });
      const doc = r.data.doc;
      if (doc) {
        setDetailNum((prev) => ({ ...(prev || {}), ...doc }));
        setItems((prev) => prev.map((x) => (x.id === doc.id ? { ...x, ...doc } : x)));
        setIndagaAgenti({
          num456: campi.includes('numero') && doc.numero_pulito !== numeroPrima,
          copertura: statoPrima !== 'completo' && doc.copertura_indirizzo_stato === 'completo',
        });
      }
      (r.data.avvisi || []).forEach((a) => toast.warning(a));
      const nApp = (r.data.aggiornati || []).length;
      setIndagaApplied(true);
      if (nApp) toast.success(`Aggiornati ${nApp} campi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento');
    }
    setIndagaBusy(false);
  };


  // ── selezione multipla ──
  const toggleRow = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pageIds = items.map(i => i.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const toggleAllPage = () => setSelected(prev => {
    const n = new Set(prev);
    if (allPageSelected) pageIds.forEach(id => n.delete(id)); else pageIds.forEach(id => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const clearSelAll = () => { setSelected(new Set()); setSelectAllFiltered(false); };
  // numeri selezionati visibili (per il riepilogo nella finestra di eliminazione)
  const previewNumbers = items.filter(i => selected.has(i.id)).map(i => i.numero_pulito).slice(0, 12);
  // colonne opzionali in base ai permessi (per il colSpan delle righe vuote)
  const nCols = 11 + (can('ssmate') ? 1 : 0) + (mostraCopertura ? 1 : 0);

  const buildFiltro = () => ({
    risultato: filter, q, provincia: provinciaFilter, regione: regioneFilter,
    tipo_impresa: tipoImpresaFilter, lavorazione: lavorazioneFilter,
    export_stato: exportStato, source: sourceFilter, indirizzo_stato: indirizzoStato, tim_inattivo: timInattivo,
    bug_456: bug456, bug_copertura: bugCopertura, ftth_vicino: ftthVicino,
  });

  // Verifica 456 sullo scope scelto: selezione manuale / pagina, oppure tutti i record del filtro.
  const runVerify456 = async () => {
    setBulkBusy(true);
    try {
      let total; let jobId456 = null;
      if (selectAllFiltered) {
        // `usa_filtro` = esattamente i record che vedi a schermo. La vecchia strada
        // (`all_pending`) cercava solo i MAI verificati e su una lista di record gia'
        // verificati rispondeva zero su zero.
        const r = await axios.post(`${API}/verify-bulk`, { usa_filtro: true, filtro: buildFiltro() });
        total = r.data.total; jobId456 = r.data.job_id;
      } else {
        const r = await axios.post(`${API}/verify-bulk`, { numero_ids: [...selected] });
        total = r.data.total ?? selected.size; jobId456 = r.data.job_id;
      }
      avviaBulk('456', { jobId: jobId456, total });
      toast.success(`Verifica 456 avviata su ${total} numeri`);
      clearSelAll(); onJobStarted?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setBulkBusy(false);
  };

  // Verifica copertura sullo scope scelto.
  const runCopertura = async () => {
    setBulkBusy(true);
    try {
      const payload = selectAllFiltered
        ? { usa_filtro: true, filtro: buildFiltro() }
        : { numero_ids: [...selected] };
      const r = await axios.post(`${API}/copertura-bulk`, payload);
      avviaBulk('copertura', { jobId: r.data.job_id, total: r.data.total });
      toast.success(`Verifica copertura avviata su ${r.data.total} numeri`);
      clearSelAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore copertura'); }
    setBulkBusy(false);
  };

  // AI SSMATE sulla selezione (o sul filtro): verifica presidio commerciale.
  const runSsmate = async () => {
    setBulkBusy(true);
    try {
      const payload = selectAllFiltered
        ? { usa_filtro: true, filtro: buildFiltro() }
        : { numero_ids: [...selected] };
      const r = await axios.post(`${API}/ssmate/bulk`, payload);
      if (r.data.job_id) { avviaBulk('ssmate', { jobId: r.data.job_id, total: r.data.total }); toast.success(`AI SSMATE avviato su ${r.data.total} aziende`); }
      else toast.info(r.data.message || 'Nessuna azienda con CF valido da verificare');
      clearSelAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore SSMATE'); }
    setBulkBusy(false);
  };

  // Verifica VF (Vodafone) sullo scope scelto.
  const runVf = async () => {
    setBulkBusy(true);
    try {
      const payload = selectAllFiltered
        ? { usa_filtro: true, filtro: buildFiltro() }
        : { numero_ids: [...selected] };
      const r = await axios.post(`${API}/gea/bulk`, payload);
      setVfJobId(r.data.job_id);
      avviaBulk('vf', { jobId: r.data.job_id, total: r.data.total });
      toast.success(`Verifica VF avviata su ${r.data.total} numeri`);
      clearSelAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore VF'); }
    setBulkBusy(false);
  };

  // Indaga sui record con INDIRIZZO INCOMPLETO: recupera via+civico dal numero (Google Maps) e li
  // completa (P.IVA confermata oppure via gia' coerente). Marca i record e accende l'agente Indaga.
  const runIndaga = async () => {
    setBulkBusy(true);
    try {
      const payload = selectAllFiltered
        ? { usa_filtro: true, filtro: buildFiltro() }
        : { numero_ids: [...selected] };
      const r = await axios.post(`${API}/indaga/bulk`, payload);
      if (r.data.total) { avviaBulk('indaga', { total: r.data.total }); toast.success(`Indaga avviata su ${r.data.total} record con indirizzo incompleto`); }
      else toast.info('Nessun record con indirizzo incompleto tra quelli scelti');
      clearSelAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore Indaga'); }
    setBulkBusy(false);
  };

  // Esporta i lead (NON TIM + fibra) dell'area filtrata come lotto + download
  const esportaLead = async () => {
    try {
      const filtri = {};
      if (provinciaFilter) filtri.provincia = provinciaFilter;
      if (tipoImpresaFilter) filtri.tipo_impresa = tipoImpresaFilter;
      const r = await axios.post(`${API}/export/genera`, {
        filtri,
        regioni: regioneFilter ? [regioneFilter] : [],
        province: provinciaFilter ? [provinciaFilter] : [],
        formato: 'unico', solo_lead: true,
        nota: `Lead fibra ${new Date().toLocaleDateString('it-IT')}`,
      });
      const lotto = r.data?.lotto;
      if (lotto?.id) {
        toast.success(`Esporto ${lotto.totale_numeri ?? ''} lead…`);
        window.open(mediaUrl(`/export/lotti/${lotto.id}/download`), '_blank');
      } else {
        toast.info('Nessun lead disponibile per l\'export');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore export lead'); }
  };

  // Apre la finestra di conferma (niente confirm del browser): l'utente deve scrivere ELIMINA.
  const deleteSelected = () => { setDelText(''); setDelOpen(true); };
  const doDeleteSelected = async () => {
    if (delText.trim() !== 'ELIMINA') return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/numeri/bulk-delete`, { ids: [...selected] });
      toast.success(`${r.data.deleted} numeri eliminati`);
      setDelOpen(false); clearSel(); onDeleted?.(); load();
    } catch { toast.error('Errore eliminazione'); }
    setBulkBusy(false);
  };

  // ── ordinamento ──
  const setSort = (col) => {
    if (sortBy === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };
  const sortArrow = (col) => (sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const renderResultBadge = (r) => {
    if (!r) return <span className="badge PENDING">In attesa</span>;
    const cls = r.replace(' ', '-');
    return <span className={`badge ${cls}`}>{r}</span>;
  };

  return (
    <>
      {/* ── Sezione FILTRI (tra l'header e la lista) ── */}
      <div className="card numeri-filters">
        <div className="nf-head">
          <IconFilter size={15} /> <span>Filtri</span>
          <span className="nf-count">{total.toLocaleString('it-IT')} numeri</span>
        </div>
        <div className="numeri-toolbar">
        {/* Search */}
        <div className="nt-search">
          <IconSearch size={15} color="var(--faint)" />
          <input
            type="text"
            placeholder="Cerca numero, azienda…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {/* Pulsante Filtri (apre il dialog) */}
        <button className="nt-filtri-btn" onClick={() => { setFiltriOpen(true); setFiltroTab('generale'); }}>
          <IconFilter size={14} /> Filtri{activeFiltri > 0 && <span className="nt-filtri-badge">{activeFiltri}</span>}
        </button>
        {activeFiltri > 0 && (
          <button className="nt-clear" onClick={clearAll}>Azzera</button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {(() => {
            const selCount = selected.size;
            const attivo = selCount > 0 || selectAllFiltered;
            return (
              <div style={{ position: 'relative' }}>
                <button
                  className="btn btn-secondary"
                  disabled={!attivo}
                  onClick={() => setToolsOpen(o => !o)}
                  title={attivo ? 'Strumenti sulla selezione' : 'Seleziona almeno un numero per usare gli strumenti'}
                  style={{ opacity: attivo ? 1 : 0.45 }}
                >
                  <IconTools size={15} />
                </button>
                {toolsOpen && attivo && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setToolsOpen(false)} />
                    <div style={{
                      position: 'absolute', right: 0, top: '112%', zIndex: 50, background: 'var(--surface)',
                      border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,.16)',
                      padding: 12, minWidth: 260, textAlign: 'left',
                    }}>
                      <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, marginBottom: 8 }}>
                        {selectAllFiltered ? `Tutti i ${total.toLocaleString('it-IT')} record del filtro` : `${selCount} selezionati`}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button className="btn btn-primary" disabled={bulkBusy || disabled} onClick={() => { setToolsOpen(false); runVerify456(); }}>
                          <IconSearch size={14} /> Verifica 456
                        </button>
                        {mostraCopertura && (
                          <button className="btn btn-secondary" disabled={bulkBusy} onClick={() => { setToolsOpen(false); runCopertura(); }}>
                            <IconSignal size={14} /> Verifica copertura
                          </button>
                        )}
                        {can('ssmate') && (
                          <button className="btn btn-secondary" disabled={bulkBusy} onClick={() => { setToolsOpen(false); runSsmate(); }}>
                            <IconBuilding size={14} /> Verifica SSMATE
                          </button>
                        )}
                        {can('gea') && (
                          <button className="btn btn-secondary" disabled={bulkBusy} onClick={() => { setToolsOpen(false); runVf(); }}>
                            <VodafoneBadge size={14} /> AI VF (Vodafone)
                          </button>
                        )}
                        {can('indaga') && (
                          <button className="btn btn-secondary" disabled={bulkBusy} onClick={() => { setToolsOpen(false); runIndaga(); }}
                            title="Recupera l'indirizzo mancante col numero (Google Maps). Solo record con indirizzo incompleto.">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><circle cx="10.5" cy="10.5" r="6.7" /><path d="m20.5 20.5-4.7-4.7" /></svg> Indaga indirizzo
                          </button>
                        )}
                        {!selectAllFiltered && (
                          <button className="btn btn-secondary" disabled={bulkBusy} onClick={() => { setToolsOpen(false); deleteSelected(); }}>
                            <IconTrash size={14} /> Elimina
                          </button>
                        )}
                        <button className="nt-clear" onClick={() => { setToolsOpen(false); clearSelAll(); }}>Deseleziona</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <div style={{ position: 'relative' }}>
            <button className="btn btn-secondary" onClick={() => setListsOpen(o => !o)}
              disabled={savedLists.length === 0} title="Liste salvate"
              style={{ opacity: savedLists.length ? 1 : 0.45 }}>
              <IconList size={15} />
            </button>
            {listsOpen && savedLists.length > 0 && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setListsOpen(false)} />
                <div style={{ position: 'absolute', right: 0, top: '112%', zIndex: 50, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,.16)', padding: 8, minWidth: 240, textAlign: 'left' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', padding: '2px 8px 6px' }}>Liste salvate</div>
                  {savedLists.map((l) => (
                    <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button onClick={() => { applyList(l.id); setListsOpen(false); }}
                        style={{ flex: 1, textAlign: 'left', background: selectedListId === l.id ? 'var(--surface-2)' : 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '6px 8px', borderRadius: 8, color: 'var(--muted)' }}>{l.nome}</button>
                      <button title="Elimina" onClick={() => deleteList(l.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', padding: 4, display: 'inline-flex' }}><IconTrash size={14} /></button>
                    </div>
                  ))}
                  {selectedListId && (
                    <button onClick={() => { applyList(''); setListsOpen(false); }}
                      style={{ width: '100%', marginTop: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--muted)', padding: '6px 8px', textAlign: 'left' }}>✕ Deseleziona</button>
                  )}
                </div>
              </>
            )}
          </div>
          <button className="btn btn-secondary" onClick={saveList} title="Salva i filtri attuali come lista">
            <IconSave size={15} />
          </button>
          {leadFilter && total > 0 && (
            <button className="btn btn-primary" onClick={esportaLead} title="Esporta i lead (NON TIM coperti da fibra) in Excel">
              ⭐ Esporta lead
            </button>
          )}
        </div>
      </div>
      </div>

      {/* ── Dialog FILTRI ── */}
      {filtriOpen && (
        <div className="nt-overlay" onClick={() => setFiltriOpen(false)}>
          <div className="nfd" onClick={(e) => e.stopPropagation()}>
            <div className="nfd-head">
              <span><IconFilter size={16} /> Filtri{activeFiltri > 0 ? ` · ${activeFiltri}` : ''}</span>
              <button className="nfd-x" onClick={() => setFiltriOpen(false)}><IconClose size={16} /></button>
            </div>
            <div className="nfd-body">
              {/* ── Barra schede ── */}
              <div className="nfd-tabs">
                <button className={filtroTab === 'generale' ? 'on' : ''} onClick={() => setFiltroTab('generale')}>Generale</button>
                <button className={filtroTab === '456' ? 'on' : ''} onClick={() => setFiltroTab('456')}>456</button>
                {mostraCopertura && <button className={filtroTab === 'copertura' ? 'on' : ''} onClick={() => setFiltroTab('copertura')}>Copertura</button>}
                {can('ssmate') && <button className={filtroTab === 'ssmate' ? 'on' : ''} onClick={() => setFiltroTab('ssmate')}>SSMATE</button>}
                {can('gea') && <button className={filtroTab === 'vf' ? 'on' : ''} onClick={() => setFiltroTab('vf')}>VF</button>}
              </div>

              {/* ── Pannello GENERALE: identità & stato ── */}
              {filtroTab === 'generale' && (
                <div className="nfd-panel">
                  <div className="nfd-sec">Identità</div>
                  <div className="nfd-stack">
                    <label>Tipo
                      <select value={tipoImpresaFilter} onChange={(e) => setTipoImpresaFilter(e.target.value)}>
                        <option value="">Tutti</option><option value="azienda">Azienda</option><option value="privato">Privato</option>
                      </select>
                    </label>
                    <label>Regione
                      <select value={regioneFilter} onChange={(e) => { setRegioneFilter(e.target.value); setProvinciaFilter(''); }}>
                        <option value="">Tutte</option>
                        {regioni.map(r => <option key={r.nome} value={r.nome}>{r.nome} ({r.count})</option>)}
                      </select>
                    </label>
                    <label>Provincia
                      <select value={provinciaFilter} onChange={(e) => setProvinciaFilter(e.target.value)}>
                        <option value="">Tutte</option>
                        {province.filter(p => !regioneFilter || p.regione === regioneFilter).map(p => <option key={p.nome} value={p.nome}>{p.nome} ({p.count})</option>)}
                      </select>
                    </label>
                    <label>Lista di origine
                      <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                        <option value="">Tutte</option>
                        {sources.map((s) => <option key={s.nome} value={s.nome}>{s.nome} ({s.count})</option>)}
                      </select>
                    </label>
                    <label>Fonte
                      <select value={fonteFilter} onChange={(e) => setFonteFilter(e.target.value)}>
                        <option value="">Tutte</option>
                        <option value="dominio">Verifica dominio</option>
                        <option value="vicine">Vicine (aziende in zona)</option>
                        <option value="indaga">Indaga</option>
                      </select>
                    </label>
                    <label>Completezza indirizzo
                      <select value={indirizzoStato} onChange={(e) => setIndirizzoStato(e.target.value)}>
                        <option value="">Tutti</option>
                        <option value="completo">Completo</option>
                        <option value="incompleto">Incompleto</option>
                        <option value="manca_civico">Manca civico</option>
                        <option value="manca_via">Manca via</option>
                        <option value="senza_indirizzo">Senza indirizzo</option>
                      </select>
                    </label>
                  </div>

                  <div className="nfd-sec">Stato lavorazione</div>
                  <div className="nfd-stack">
                    <label>Lavorazione
                      <select value={lavorazioneFilter} onChange={(e) => setLavorazioneFilter(e.target.value)}>
                        <option value="">Tutti</option><option value="da_lavorare">Da lavorare</option><option value="lavorato">Lavorati</option>
                      </select>
                    </label>
                    <label>Stato export
                      <select value={exportStato} onChange={(e) => setExportStato(e.target.value)}>
                        <option value="">Tutti</option>
                        <option value="esportati">Esportati</option>
                        <option value="mai">Mai esportati</option>
                      </select>
                    </label>
                  </div>
                  <div className="nfd-checks">
                    <label className="nfd-chk"><input type="checkbox" checked={leadFilter} onChange={(e) => setLeadFilter(e.target.checked)} /> Solo lead (NON TIM + fibra)</label>
                    <label className="nfd-chk"><input type="checkbox" checked={soloDaRivedere} onChange={(e) => setSoloDaRivedere(e.target.checked)} /> Solo da rivedere</label>
                    <label className="nfd-chk"><input type="checkbox" checked={timInattivo} onChange={(e) => setTimInattivo(e.target.checked)} /> Solo TIM inattivi</label>
                  </div>
                </div>
              )}

              {/* ── Pannello 456 ── */}
              {filtroTab === '456' && (
                <div className="nfd-panel">
                  <div className="nfd-sec">AI 456</div>
                  <div className="nfd-stack">
                    <label>Esito 456
                      <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                        <option value="">Tutti</option><option value="PENDING">In attesa</option>
                        <option value="TIM">TIM</option><option value="NON TIM">NON TIM</option><option value="ERRORE">Errore</option>
                      </select>
                    </label>
                    <label>Ri-verifica 456
                      <select value={scadutiFilter} onChange={(e) => setScadutiFilter(e.target.value)}>
                        <option value="">Tutti</option><option value="si">Da ri-verificare ({scadutiCount.toLocaleString('it-IT')})</option>
                      </select>
                    </label>
                  </div>
                  <div className="nfd-checks">
                    <label className="nfd-chk"><input type="checkbox" checked={daFare456} onChange={(e) => setDaFare456(e.target.checked)} /> <IconPhone size={13} /> Da fare · 456</label>
                    <label className="nfd-chk" title="I record che il 456 aveva deciso con un numero diverso dal loro, prima della correzione: da ri-verificare in blocco.">
                      <input type="checkbox" checked={bug456} onChange={(e) => setBug456(e.target.checked)} /> <IconPhone size={13} /> Bug 456 · da ri-verificare</label>
                  </div>
                </div>
              )}

              {/* ── Pannello COPERTURA ── */}
              {filtroTab === 'copertura' && mostraCopertura && (
                <div className="nfd-panel">
                  <div className="nfd-sec">Copertura</div>
                  <div className="nfd-stack">
                    <label>Tecnologia
                      <select value={coperturaFilter} onChange={(e) => setCoperturaFilter(e.target.value)}>
                        <option value="">Tutte</option>
                        <optgroup label="FTTH (fibra)">
                          <option value="FTTH:10000">FTTH · 10 Giga</option><option value="FTTH:2500">FTTH · 2,5 Giga</option>
                          <option value="FTTH:1000">FTTH · 1 Giga</option><option value="FTTH">FTTH · tutte</option>
                        </optgroup>
                        <option value="FTTC">FTTC</option><option value="FWA">FWA</option><option value="ADSL">ADSL</option>
                      </select>
                    </label>
                    <label>Esito copertura
                      <select value={coperturaEsito} onChange={(e) => setCoperturaEsito(e.target.value)}>
                        <option value="">Tutti</option>
                        <option value="COPERTO">Coperto</option><option value="NON COPERTO">Non coperto</option>
                        <option value="NON CENSITO">Non censito</option><option value="ERRORE">Errore</option>
                        <option value="__none__">Non verificato</option>
                      </select>
                    </label>
                    <label>Diventati FTTH
                      <select value={coperturaNew ? '1' : ''} onChange={(e) => setCoperturaNew(e.target.value === '1')}>
                        <option value="">Tutti</option><option value="1">Solo NEW (passati a FTTH)</option>
                      </select>
                    </label>
                  </div>
                  <div className="nfd-checks">
                    <label className="nfd-chk"><input type="checkbox" checked={daFareCop} onChange={(e) => setDaFareCop(e.target.checked)} /> <IconSignal size={13} /> Da fare · Copertura</label>
                    <label className="nfd-chk" title="I record risultati «non censiti» perché la via era scritta abbreviata (es. G. Mazzini invece di Giuseppe Mazzini), prima della correzione: da ri-verificare in blocco. Escono dalla lista a ogni nuova verifica.">
                      <input type="checkbox" checked={bugCopertura} onChange={(e) => setBugCopertura(e.target.checked)} /> <IconSignal size={13} /> Bug copertura · da ri-verificare</label>
                    {/* Clienti su una via dove TIM ha appena aperto la fibra: l'agente
                        li rimette in coda per primi, fuori dal timing. */}
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="checkbox" checked={ftthVicino} onChange={(e) => setFtthVicino(e.target.checked)} /> <IconSignal size={13} /> Vicino a FTTH nuova</label>
                  </div>
                </div>
              )}

              {/* ── Pannello SSMATE ── */}
              {filtroTab === 'ssmate' && (
                <div className="nfd-panel">
                  <div className="nfd-sec">SSMATE · presidio</div>
                  <div className="nfd-stack">
                    <label>Lucchetto
                      <select value={ssmateLucchetto} onChange={(e) => setSsmateLucchetto(e.target.value)}>
                        <option value="">Tutti</option>
                        <option value="GRIGIO">Grigio (libera)</option>
                        <option value="VERDE">Verde (cliente TIM)</option>
                        <option value="NERO">Nero (presidiata)</option>
                        <option value="VIOLA">Viola (presidiata)</option>
                        <option value="ROSSO">Rosso</option>
                        <option value="GIALLO">Giallo</option>
                        <option value="ARANCIONE">Arancione</option>
                        <option value="__none__">Non verificato</option>
                      </select>
                    </label>
                  </div>
                  <div className="nfd-checks">
                    <label className="nfd-chk"><input type="checkbox" checked={daFareSsm} onChange={(e) => setDaFareSsm(e.target.checked)} /> <IconBuilding size={13} /> Da fare · SSMATE</label>
                  </div>
                </div>
              )}

              {/* ── Pannello VF (Vodafone) ── */}
              {filtroTab === 'vf' && (
                <div className="nfd-panel">
                  <div className="nfd-sec">Vodafone</div>
                  <div className="nfd-stack">
                    <label>Esito VF
                      <select value={geaVodafone} onChange={(e) => setGeaVodafone(e.target.value)}>
                        <option value="">Tutti</option>
                        <option value="si">Solo Vodafone</option>
                        <option value="no">Escludi Vodafone</option>
                      </select>
                    </label>
                  </div>
                  <div className="nfd-checks">
                    <label className="nfd-chk"><input type="checkbox" checked={daFareGea} onChange={(e) => setDaFareGea(e.target.checked)} /> Da fare · VF (Vodafone)</label>
                  </div>
                </div>
              )}

            </div>
            <div className="nfd-foot">
              <button className="btn btn-secondary" onClick={clearAll}>Azzera tutto</button>
              <button className="btn btn-primary" onClick={() => setFiltriOpen(false)}>Mostra {total.toLocaleString('it-IT')} numeri</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sezione LISTA ── */}
      <div className="card">
      {/* Barra della selezione: sta SOPRA la lista, non dentro il menu strumenti, perche'
          la scelta fra "questa pagina" e "tutti i record filtrati" va fatta guardando la
          lista, prima di scegliere lo strumento. */}
      {(selected.size > 0 || selectAllFiltered) && (
        <div className="nt-selbar">
          <span className="nt-selbar-txt">
            {selectAllFiltered
              ? <>Selezionati <b>tutti i {total.toLocaleString('it-IT')} record filtrati</b></>
              : <><b>{selected.size.toLocaleString('it-IT')}</b> selezionati in questa pagina</>}
          </span>
          {!selectAllFiltered && total > selected.size && (
            <button type="button" className="nt-selbar-az" onClick={() => setSelectAllFiltered(true)}>
              Seleziona tutti i {total.toLocaleString('it-IT')} record filtrati
            </button>
          )}
          <button type="button" className="nt-selbar-az nt-selbar-annulla" onClick={clearSelAll}>
            Annulla selezione
          </button>
        </div>
      )}
      <div style={{ overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 32 }}><input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} title="Seleziona pagina" /></th>
              <th className="nt-sort" onClick={() => setSort('numero_pulito')}>Pulito{sortArrow('numero_pulito')}</th>
              <th>Ragione Sociale</th>
              <th className="nt-sort" onClick={() => setSort('provincia_prefisso')}>Provincia{sortArrow('provincia_prefisso')}</th>
              <th>Tipo</th>
              <th className="nt-sort" onClick={() => setSort('risultato')}>Risultato{sortArrow('risultato')}</th>
              <th>Lavorazione</th>
              {mostraCopertura && <th>Tecnologia</th>}
              {can('ssmate') && <th>SSMATE</th>}
              <th className="nt-sort" onClick={() => setSort('verified_at')}>Verificato{sortArrow('verified_at')}</th>
              <th className="nt-sort" onClick={() => setSort('imported_at')}>Importato{sortArrow('imported_at')}</th>
              <th>Audio</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={nCols} style={{ textAlign: 'center', padding: 24 }}><IconSpinner size={14} /> Caricamento...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={nCols} style={{ textAlign: 'center', padding: 24, color: 'var(--faint)' }}>
                Nessun numero. Carica un Excel per iniziare.
              </td></tr>
            )}
            {items.map((n) => (
              <tr key={n.id} onClick={() => setDetailNum(n)}
                style={{ cursor: 'pointer', background: selected.has(n.id) ? 'rgba(232,96,10,0.06)' : undefined }}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(n.id)} onChange={() => toggleRow(n.id)} />
                </td>
                <td><code style={{ fontSize: 12 }}>{n.numero_pulito}</code></td>
                <td style={{ fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={n.ragione_sociale || ''}>
                  {n.ragione_sociale || '—'}
                </td>
                <td style={{ fontSize: 12 }}>{n.provincia_prefisso || '—'}</td>
                <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{n.tipo_impresa || '—'}</td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {renderResultBadge(n.risultato)}
                    {can('gea') && n.gea_risultato === 'VODAFONE' && <VodafoneBadge size={15} />}
                  </span>
                  {n.numero_verificato && n.numero_verificato !== n.numero_pulito && (
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                      → {n.numero_verificato}
                    </div>
                  )}
                  {n.tim456_numerazione_inattiva && (
                    <div style={{ fontSize: 10, color: 'var(--warn)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}><IconWarning size={12} /> Non attiva</div>
                  )}
                  {isScaduto(n) && (
                    <div style={{ fontSize: 10, color: 'var(--warn)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3, fontWeight: 700 }}><IconRefresh size={11} /> Da ri-verificare</div>
                  )}
                </td>
                <td>
                  {n.stato_lavorazione === 'da_lavorare' && (
                    <span style={{ background: 'rgba(232,96,10,0.12)', color: 'var(--accent)', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>Da lavorare</span>
                  )}
                  {n.stato_lavorazione === 'lavorato' && (
                    <span style={{ background: 'rgba(46,204,113,0.12)', color: 'var(--ok)', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>Lavorato</span>
                  )}
                  {n.stato_lavorazione === 'ridisponibile' && (
                    <span style={{ background: 'rgba(72,149,239,0.12)', color: 'var(--info)', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>Ri-disponibile</span>
                  )}
                  {!n.stato_lavorazione && (
                    <span style={{ color: 'var(--faint)' }}>—</span>
                  )}
                </td>
                {mostraCopertura && (
                  <td style={{ fontSize: 12 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {n.copertura_tecnologia
                        ? <span style={{ fontWeight: 700 }}>{n.copertura_tecnologia}</span>
                        : <span style={{ color: 'var(--line)' }}>—</span>}
                      {n.copertura_new && <span title={`Passato a FTTH${n.copertura_ftth_da ? ` da ${n.copertura_ftth_da}` : ''}`} style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: 'var(--ok)', borderRadius: 5, padding: '1px 5px', letterSpacing: '.04em' }}>NEW</span>}
                    </span>
                  </td>
                )}
                {can('ssmate') && (
                  <td onClick={(e) => e.stopPropagation()}>
                    {n.ssmate_lucchetto ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span title={`${n.ssmate_lucchetto} — ${n.ssmate_lavorabile ? 'lavorabile' : 'non lavorabile'}${n.ssmate_consistenza ? ' · ' + n.ssmate_consistenza : ''}`}
                          style={{ width: 13, height: 13, borderRadius: '50%', background: LUCCHETTO_HEX[n.ssmate_lucchetto] || 'var(--faint)',
                            border: n.ssmate_lucchetto === 'NON TROVATO' ? '1px solid var(--faint)' : 'none', display: 'inline-block', cursor: 'help' }} />
                        {n.ssmate_dettaglio && Object.keys(n.ssmate_dettaglio).length > 0 && (
                          <button onClick={() => setSsRecapNum(n)} title="Apri riepilogo consistenze"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--info)', display: 'inline-flex' }}>
                            <IconList size={13} />
                          </button>
                        )}
                      </span>
                    ) : <span style={{ color: 'var(--line)' }}>—</span>}
                  </td>
                )}
                <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {n.verified_at ? new Date(n.verified_at).toLocaleString('it-IT') : '—'}
                </td>
                <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {n.imported_at ? new Date(n.imported_at).toLocaleDateString('it-IT') : '—'}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {n.audio_file && (
                    <AudioPlayer src={mediaUrl(`/audio/${encodeURIComponent(n.audio_file)}`)} />
                  )}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', width: '100%' }}>
                    {/* 456 (cornetta, verde): attivo se mai verificato o timing scaduto */}
                    {(() => {
                      const busy = verifyingSingle === n.id;
                      const ok = canVerify(n) && !disabled;
                      const nextDate = nextVerifyDate(n);
                      return (
                        <button
                          style={{ padding: '4px 8px', fontSize: 11, background: 'none', border: 'none', boxShadow: 'none',
                            cursor: (busy || !ok) ? 'not-allowed' : 'pointer', color: ok ? 'var(--ok)' : 'var(--line)',
                            display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => handleVerifySingle(n.id, n.numero_pulito)}
                          disabled={busy || !ok}
                          title={!ok && nextDate
                            ? `Ri-verifica dal ${nextDate.toLocaleDateString('it-IT')} (timing ${timingDays} giorni)`
                            : 'Verifica 456 ora'}
                        >
                          {busy ? <IconSpinner size={14} /> : <IconPhone size={14} />}
                        </button>
                      );
                    })()}
                    {/* Copertura (segnale, blu): attivo se mai verificato o timing scaduto.
                        Indirizzo incompleto -> icona BARRATA (ambra) + tooltip dedicato. */}
                    {mostraCopertura && (() => {
                      const busy = copBusyId === n.id;
                      const stato = n.copertura_indirizzo_stato;
                      const indirizzoIncompleto = (!!stato && stato !== 'completo') || !n.comune || !n.indirizzo;
                      const ok = !indirizzoIncompleto && coperturaDaFare(n) && !disabled;
                      return (
                        <button
                          style={{ padding: '4px 8px', fontSize: 11, background: 'none', border: 'none', boxShadow: 'none',
                            cursor: (busy || !ok) ? 'not-allowed' : 'pointer',
                            color: indirizzoIncompleto ? 'var(--warn)' : (ok ? 'var(--info)' : 'var(--line)'),
                            display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => handleVerifyCopertura(n.id)}
                          disabled={busy || !ok}
                          title={indirizzoIncompleto
                            ? 'Indirizzo incompleto — impossibile verificare la copertura'
                            : (ok ? 'Verifica copertura TIM' : `Copertura già verificata (timing ${copTiming} giorni)`)}
                        >
                          {busy ? <IconSpinner size={14} />
                            : indirizzoIncompleto ? (
                              <span style={{ position: 'relative', display: 'inline-flex' }}>
                                <IconSignal size={14} />
                                <span style={{ position: 'absolute', left: '50%', top: '50%', width: 17, height: 1.6,
                                  background: 'currentColor', transform: 'translate(-50%,-50%) rotate(-45deg)', borderRadius: 2 }} />
                              </span>
                            ) : <IconSignal size={14} />}
                        </button>
                      );
                    })()}
                    {/* SSMATE (edificio, giallo): attivo se CF valido e (mai verificato o timing scaduto) */}
                    {can('ssmate') && (() => {
                      const cfOk = isCfValido(n.codice_fiscale);
                      const busy = ssmateBusyId === n.id;
                      const ok = cfOk && ssmateDaFare(n) && !disabled;
                      return (
                        <button
                          style={{ padding: '4px 8px', fontSize: 11, background: 'none', border: 'none', boxShadow: 'none',
                            cursor: (busy || !ok) ? 'not-allowed' : 'pointer', color: ok ? 'var(--warn)' : 'var(--line)',
                            display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => verificaSsmate(n.id)}
                          disabled={busy || !ok}
                          title={!cfOk ? 'Codice Fiscale mancante o non valido'
                            : (ok ? 'Verifica presidio commerciale (SSMATE)' : `Azienda già verificata (timing ${ssTiming} giorni)`)}
                        >
                          {busy ? <IconSpinner size={14} /> : <IconBuilding size={14} />}
                        </button>
                      );
                    })()}
                    {/* GEA (logo Vodafone): solo NON TIM fissi, attivo se da fare per timing */}
                    {can('gea') && (() => {
                      const busy = geaBusyId === n.id;
                      const ok = geaDaFare(n) && !disabled;
                      if (!ok && !busy) return null;   // VODAFONE già fatto -> mostrato accanto al risultato
                      return (
                        <button
                          style={{ padding: '4px 6px', background: 'none', border: 'none', boxShadow: 'none',
                            cursor: busy ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => verificaGea(n.id)} disabled={busy}
                          title="Verifica Vodafone"
                        >
                          {busy ? <IconSpinner size={14} /> : <VodafoneBadge size={14} style={{ filter: 'grayscale(1)', opacity: 0.55 }} />}
                        </button>
                      );
                    })()}
                    {n.social && Object.keys(n.social).length > 0 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 'auto', paddingLeft: 8 }}>
                        <SocialLinks social={n.social} size={14} gap={6} />
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="pagination">
          <button onClick={() => setPage(1)} disabled={page <= 1}>«</button>
          <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}>‹</button>
          <span className="info">Pagina {page} / {pages}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= pages}>›</button>
          <button onClick={() => setPage(pages)} disabled={page >= pages}>»</button>
        </div>
      )}
      </div>

      {detailNum && (() => {
        const d = detailNum;
        const det = d.ssmate_dettaglio || {};
        const consTot = ssConsTotale(det);
        const isLead = d.risultato === 'NON TIM';
        const stato = d.copertura_indirizzo_stato;
        const indirizzoIncompleto = (!!stato && stato !== 'completo') || !d.comune || !d.indirizzo;
        const hasSocial = d.social && Object.keys(d.social).length > 0;
        const LINE = 'var(--line)', LINE2 = 'var(--line)', FAINT = 'var(--faint)', MUTED = 'var(--muted)';
        const GREEN = 'var(--ok)', BLUE = 'var(--info)', AMBER = 'var(--warn)';
        const secStyle = (ac) => ({ background: 'var(--surface)', border: `1px solid ${LINE}`, borderLeft: `3px solid ${ac}`, borderRadius: 13, boxShadow: '0 1px 2px rgba(15,23,42,.05)', overflow: 'hidden', display: 'flex', flexDirection: 'column' });
        const secH = { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', borderBottom: `1px solid ${LINE2}`, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 800, color: 'var(--muted)' };
        const secB = { padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 };
        const tag = (bg, fg) => ({ marginLeft: 'auto', fontSize: 10, fontWeight: 800, padding: '2px 9px', borderRadius: 11, background: bg, color: fg });
        const kS = { color: FAINT, fontSize: 12, fontWeight: 500 };
        const icBtn = { width: 25, height: 25, borderRadius: 7, border: `1px solid ${LINE}`, background: 'var(--surface)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(15,23,42,.05)', color: 'var(--muted)' };
        const rc = resColor(d.risultato);
        const chip = (bg, fg, txt) => <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 11px', borderRadius: 20, background: bg, color: fg }}>{txt}</span>;
        // fonte con cui Indaga ha completato l'indirizzo (SOLO admin): quanto e' "forte" il recupero
        const FONTE_INDAGA = {
          piva: { l: 'P.IVA', bg: 'rgba(34,197,94,.15)', fg: GREEN, t: 'Indirizzo recuperato da Indaga con match Partita IVA (100% certo)' },
          via_coerente: { l: 'via coerente', bg: 'rgba(59,130,246,.15)', fg: BLUE, t: 'Indirizzo completato da Indaga: la via combaciava, aggiunto il civico mancante' },
          toponomastica: { l: 'toponomastica', bg: 'rgba(245,158,11,.16)', fg: AMBER, t: 'Indirizzo confermato da Indaga tramite toponomastica TIM (grafia ufficiale)' },
        };
        const fonteInd = isAdmin && d.indaga_fonte_scrittura ? FONTE_INDAGA[d.indaga_fonte_scrittura] : null;
        // icona agente nell'intestazione: attiva SOLO con la logica timing (come in riga)
        const agentBtn = (ok, busy, color, onClick, Icon, title) => (
          <button type="button" onClick={(ok && !busy) ? onClick : undefined} disabled={!ok || busy} title={title}
            style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${LINE}`, background: 'var(--surface)',
              cursor: (ok && !busy) ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', color: ok ? color : 'var(--line)', boxShadow: '0 1px 2px rgba(15,23,42,.05)' }}>
            {busy ? <IconSpinner size={14} /> : <Icon size={14} />}
          </button>
        );
        return (
          <div className="nt-overlay" onClick={() => setDetailNum(null)}>
            <div className="nt-detail" style={{ width: 720, maxWidth: '96vw' }} onClick={(e) => e.stopPropagation()}>
              <div className="nt-detail-head" style={{ display: 'block', background: isLead ? 'linear-gradient(180deg,rgba(22,163,74,.08),transparent)' : undefined }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.numero_pulito} · {d.ragione_sociale || '—'}{d.maps_categoria ? ` · ${d.maps_categoria}` : ''}
                  </div>
                  <button className="ob-x" style={{ marginLeft: 'auto' }} onClick={() => setDetailNum(null)}>✕</button>
                </div>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 11 }}>
                  {d.risultato && chip(rc.bg, rc.fg, isLead ? `${d.risultato} · Lead` : d.risultato)}
                  {d.numero_precedente && chip('rgba(100,116,139,.12)', 'var(--muted)', `ex ${d.numero_precedente}`)}
                  {d.copertura_tecnologia && chip('rgba(37,99,235,.12)', BLUE, `${d.copertura_tecnologia}${d.copertura_down ? ' ' + fmtVel(d.copertura_down) : ''}`)}
                  {d.ssmate_presidio && chip('rgba(217,119,6,.14)', AMBER, `Presidio · ${d.ssmate_presidio}`)}
                  {d.comune && chip('var(--line)', 'var(--muted)', `${d.comune}${d.provincia_prefisso ? ` (${d.provincia_prefisso})` : ''}`)}
                </div>
              </div>

              <div className="nt-detail-body" style={{ background: 'var(--surface-2)', padding: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'stretch' }}>

                  <div style={secStyle('var(--muted)')}>
                    <div style={secH}>🏢 Anagrafica</div>
                    <div style={secB}>
                      <KV>
                        <span style={kS}>Indirizzo</span><span style={{ fontWeight: 600 }}>{[d.indirizzo, d.cap, d.comune].filter(Boolean).join(' ') || '—'}
                          {fonteInd && <span title={fonteInd.t} style={{ marginLeft: 8, fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 10, background: fonteInd.bg, color: fonteInd.fg, textTransform: 'uppercase', letterSpacing: '.03em', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>Indaga · {fonteInd.l}</span>}</span>
                        {/* L'indirizzo come lo scrive il cliente sul suo sito: il nostro porta
                            la forma della toponomastica TIM, e quando cambia il toponimo
                            («VIA» per «VIALE») sembra un errore nostro (TEI SRL, 07/10/2026). */}
                        {d.indirizzo_sito && (<>
                          <span style={kS}>Sul sito</span>
                          <span style={{ fontWeight: 500, color: 'var(--muted)' }}>{d.indirizzo_sito}</span>
                        </>)}
                        <span style={kS}>Provincia</span><span style={{ fontWeight: 600 }}>{d.provincia_prefisso || '—'}</span>
                        <span style={kS}>P.IVA / CF</span><span style={{ fontWeight: 600 }}>{d.partita_iva || d.codice_fiscale || '—'}</span>
                        <span style={kS}>Tipo</span><span style={{ fontWeight: 600 }}>{d.tipo_impresa || '—'}</span>
                        {/* Chi ha portato dentro questo cliente: senza, di un record creato da
                            un agente restava solo «agente» e non si sapeva quale. */}
                        {frasiProvenienza(d).length > 0 && (<>
                          <span style={kS}>Creato da</span>
                          <span style={{ fontWeight: 600 }}>{frasiProvenienza(d).join(' ')}</span>
                        </>)}
                      </KV>
                      <div style={{ borderTop: `1px dashed ${LINE}`, margin: '2px 0' }} />
                      <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: '6px 10px', fontSize: 12, alignItems: 'center' }}>
                        <span style={kS}>🌐 Web</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {d.sito && <a href={/^https?:\/\//i.test(d.sito) ? d.sito : `https://${d.sito}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 600 }}>{String(d.sito).replace(/^https?:\/\//i, '').replace(/\/$/, '')}</a>}
                          {d.email && <a href={`mailto:${d.email}`} style={{ fontSize: 12, fontWeight: 600 }}>{d.email}</a>}
                          {hasSocial && <SocialLinks social={d.social} size={20} gap={8} />}
                          {!d.sito && !d.email && !hasSocial && <span style={{ color: FAINT, fontSize: 12 }}>nessun contatto</span>}
                          {!d.email && !hasSocial && isAdmin && (
                            <button className="btn btn-sm btn-secondary" style={{ fontSize: 11, padding: '3px 8px' }} disabled={indagaBusy} onClick={avviaIndaga}><IconSearch size={12} /> Indaga</button>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {d.campagna_prova && (() => {
                    const p = d.campagna_prova;
                    const righe = frasiProva(p);
                    const col = eRecupero(p) ? 'var(--warn)' : 'var(--muted)';
                    return (
                      <div style={{ ...secStyle(col), gridColumn: '1 / -1' }}>
                        <div style={secH}>🎯 Da campagna · {titoloProva(p)}</div>
                        <div style={secB}>
                          {righe.map((r, i) => (
                            <div key={i} style={{ fontSize: 12, lineHeight: 1.45,
                                                  fontWeight: i === righe.length - 1 ? 600 : 400 }}>{r}</div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {daElencoTim(d.copertura_origine) && (() => {
                    const righe = frasiOrigine(d.copertura_origine);
                    return (
                      <div style={{ ...secStyle('var(--info)'), gridColumn: '1 / -1' }}>
                        <div style={secH}>📡 {titoloOrigine(d.copertura_origine)}</div>
                        <div style={secB}>
                          {righe.map((r, i) => (
                            <div key={i} style={{ fontSize: 12, lineHeight: 1.45 }}>{r}</div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div style={secStyle(GREEN)}>
                    <div style={secH}>📞 Esito 456
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {agentBtn(canVerify(d), verifyingSingle === d.id, GREEN, () => handleVerifySingle(d.id, d.numero_pulito), IconPhone, canVerify(d) ? 'Verifica 456' : `Verificato di recente (timing ${timingDays}g)`)}
                        {d.audio_file && (
                          <button style={{ ...icBtn, color: audioOpen ? GREEN : 'var(--muted)' }} title="Ascolta la registrazione" onClick={() => setAudioOpen((o) => !o)}>▶</button>
                        )}
                        {d.risultato && <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 9px', borderRadius: 11, background: rc.bg, color: rc.fg }}>{d.risultato}</span>}
                        {can('gea') && (d.gea_risultato === 'VODAFONE' || geaDaFare(d) || geaBusyId === d.id) && (
                          <button type="button" disabled={geaBusyId === d.id || d.gea_risultato === 'VODAFONE'}
                            onClick={() => geaDaFare(d) && verificaGea(d.id)}
                            title={d.gea_risultato === 'VODAFONE' ? 'Linea Vodafone' : 'Verifica Vodafone'}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: d.gea_risultato === 'VODAFONE' ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                            {geaBusyId === d.id ? <IconSpinner size={14} /> : <VodafoneBadge size={16} style={d.gea_risultato === 'VODAFONE' ? undefined : { filter: 'grayscale(1)', opacity: 0.5 }} />}
                          </button>
                        )}
                      </span>
                    </div>
                    <div style={secB}>
                      <KV>
                        <span style={kS}>Verificato</span><span style={{ fontWeight: 600 }}>{d.verified_at ? new Date(d.verified_at).toLocaleString('it-IT') : '—'}</span>
                        {d.numero_verificato && d.numero_verificato !== d.numero_pulito && (<><span style={kS}>Su numero</span><span style={{ fontWeight: 600 }}>{d.numero_verificato}</span></>)}
                        <span style={kS}>Esportabile</span><span style={{ fontWeight: 700, color: d.esportabile ? GREEN : MUTED }}>{d.esportabile ? 'Sì (lead)' : 'No'}</span>
                      </KV>
                      {d.tim456_numerazione_inattiva && <div className="nt-d-warn" style={{ margin: 0 }}><IconWarning size={13} /> Numerazione non attiva</div>}
                      {audioOpen && d.audio_file && <AudioPlayer src={mediaUrl(`/audio/${encodeURIComponent(d.audio_file)}`)} />}
                    </div>
                  </div>

                  {mostraCopertura && (
                    <div style={secStyle(BLUE)}>
                      <div style={secH}>📶 Copertura TIM
                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {d.copertura_new && <span title={d.copertura_ftth_da ? `da ${d.copertura_ftth_da}` : ''} style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: GREEN, borderRadius: 6, padding: '2px 7px', letterSpacing: '.03em' }}>NEW · FTTH</span>}
                          {agentBtn(coperturaDaFare(d), copBusyId === d.id, BLUE, () => handleVerifyCopertura(d.id), IconSignal, coperturaDaFare(d) ? 'Verifica copertura' : `Verificata di recente (timing ${copTiming}g)`)}
                          {d.copertura_esito && <span style={tag('rgba(37,99,235,.12)', BLUE)}>{d.copertura_esito}</span>}
                        </span>
                      </div>
                      <div style={secB}>
                        {d.copertura_esito ? (
                          <div style={{ display: 'flex', gap: '14px 24px', flexWrap: 'wrap' }}>
                            {d.copertura_tecnologia && <Stat b={d.copertura_tecnologia} l="Tecnologia" color={BLUE} />}
                            {d.copertura_down ? <Stat b={fmtVel(d.copertura_down)} l="Download" /> : null}
                            {d.copertura_up ? <Stat b={fmtVel(d.copertura_up)} l="Upload" /> : null}
                            {d.copertura_latenza ? <Stat b={`${d.copertura_latenza} ms`} l="Latenza" /> : null}
                          </div>
                        ) : <span style={{ color: FAINT, fontSize: 12.5 }}>Non ancora verificata.</span>}
                      </div>
                    </div>
                  )}

                  {can('ssmate') && (
                    <div style={secStyle(AMBER)}>
                      <div style={secH}>🏛 Presidio SSMATE
                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {agentBtn(isCfValido(d.codice_fiscale) && ssmateDaFare(d), ssmateBusyId === d.id, AMBER, () => verificaSsmate(d.id), IconBuilding,
                            !isCfValido(d.codice_fiscale) ? 'CF mancante o non valido' : (ssmateDaFare(d) ? 'Verifica SSMATE' : `Verificato di recente (timing ${ssTiming}g)`))}
                          {d.ssmate_lucchetto && <span style={tag('var(--line)', 'var(--muted)')}>{d.ssmate_lucchetto}</span>}
                        </span>
                      </div>
                      <div style={secB}>
                        {d.ssmate_lucchetto ? (
                          <KV>
                            <span style={kS}>Lucchetto</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><LucchettoBadge value={d.ssmate_lucchetto} /><button style={icBtn} title="Apri schermata SSMATE" onClick={() => setSsShotOpen(true)}><IconImage size={14} /></button></span>
                            <span style={kS}>Lavorabile</span><span style={{ fontWeight: 700, color: d.ssmate_lavorabile ? GREEN : 'var(--err)' }}>{d.ssmate_lavorabile ? 'Sì' : 'No'}</span>
                            <span style={kS}>Consistenza</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><b style={{ fontSize: 13 }}>{consTot}</b><span style={{ color: FAINT, fontSize: 11 }}>linee/servizi</span>{Object.keys(det).length > 0 && <button style={icBtn} title="Dettaglio consistenza" onClick={() => setSsConsOpen(true)}>≣</button>}</span>
                            {d.ssmate_presidio && (<><span style={kS}>Presidio</span><span style={{ fontWeight: 600, fontSize: 12 }}>{d.ssmate_presidio}{det.partner_di_riferimento ? ` · ${det.partner_di_riferimento}` : ''}</span></>)}
                            {det.consistenze_aggiornate_al && (<><span style={kS}>Agg. al</span><span style={{ fontWeight: 600 }}>{det.consistenze_aggiornate_al}</span></>)}
                          </KV>
                        ) : !d.codice_fiscale ? <span style={{ color: FAINT, fontSize: 12.5 }}>CF mancante — SSMATE cerca per CF.</span>
                          : <span style={{ color: FAINT, fontSize: 12.5 }}>Non ancora verificato.</span>}
                      </div>
                    </div>
                  )}

                </div>

                {Array.isArray(d.storico_stati) && d.storico_stati.length > 0 && (
                  <details style={{ marginTop: 12, background: 'var(--surface)', border: `1px solid ${LINE}`, borderRadius: 13, padding: '8px 13px', fontSize: 12.5 }}>
                    <summary style={{ cursor: 'pointer', color: 'var(--muted)', fontWeight: 700 }}>Storico stati ({d.storico_stati.length})</summary>
                    <ul className="nt-d-storico" style={{ marginTop: 6 }}>
                      {d.storico_stati.map((s, i) => <li key={i}><span>{s.ts ? new Date(s.ts).toLocaleString('it-IT') : ''}</span> {s.da || '—'} → <b>{s.a || '—'}</b></li>)}
                    </ul>
                  </details>
                )}
              </div>

              {isAdmin && (indirizzoIncompleto || !hasSocial) && (
                <div style={{ borderTop: `1px solid ${LINE2}`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)' }}>
                  <span style={{ fontSize: 11.5, color: FAINT }}>{[indirizzoIncompleto && 'indirizzo incompleto', !hasSocial && 'social mancanti'].filter(Boolean).join(' · ')}</span>
                  <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} disabled={indagaBusy} onClick={avviaIndaga}><IconSearch size={13} /> Indaga</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {delOpen && (
        <div className="nt-overlay" onClick={() => !bulkBusy && setDelOpen(false)}>
          <div className="nt-detail" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="nt-detail-head">
              <span><IconTrash size={15} style={{ verticalAlign: -2, marginRight: 6 }} /> Elimina numeri selezionati</span>
              <button className="ob-x" onClick={() => setDelOpen(false)}>✕</button>
            </div>
            <div className="nt-detail-body">
              <p style={{ fontSize: 14, margin: '0 0 8px' }}>
                Stai per eliminare <b>{selected.size.toLocaleString('it-IT')}</b> numeri selezionati.
                <br /><b style={{ color: 'var(--err)' }}>Operazione irreversibile.</b>
              </p>
              {previewNumbers.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px', marginBottom: 12, maxHeight: 96, overflowY: 'auto', lineHeight: 1.9 }}>
                  {previewNumbers.map((n, i) => <span key={i} style={{ marginRight: 10, fontFamily: 'monospace' }}>{n}</span>)}
                  {selected.size > previewNumbers.length && <span style={{ color: 'var(--faint)' }}>… e altri {(selected.size - previewNumbers.length).toLocaleString('it-IT')}</span>}
                </div>
              )}
              <label style={{ fontSize: 13, fontWeight: 600 }}>Scrivi <b>ELIMINA</b> (in maiuscolo) per confermare:</label>
              <input autoFocus value={delText} onChange={(e) => setDelText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && delText.trim() === 'ELIMINA') doDeleteSelected(); }}
                placeholder="ELIMINA"
                style={{ width: '100%', marginTop: 6, padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 15, letterSpacing: '.06em', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setDelOpen(false)} disabled={bulkBusy}>Annulla</button>
                <button className="btn" disabled={delText.trim() !== 'ELIMINA' || bulkBusy} onClick={doDeleteSelected}
                  style={{ background: delText.trim() === 'ELIMINA' ? 'var(--err)' : 'var(--err)', color: '#fff', fontWeight: 700, cursor: delText.trim() === 'ELIMINA' ? 'pointer' : 'default' }}>
                  {bulkBusy ? 'Elimino…' : `Elimina ${selected.size.toLocaleString('it-IT')}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {ssRecapNum && (
        <LucchettoConsistenze
          lucchetto={ssRecapNum.ssmate_lucchetto}
          codiceFiscale={ssRecapNum.codice_fiscale}
          ragioneSociale={(ssRecapNum.ssmate_dettaglio || {}).ragione_sociale}
          lavorabile={ssRecapNum.ssmate_lavorabile}
          dettaglio={ssRecapNum.ssmate_dettaglio}
          screenshotUrl={mediaUrl(`/ssmate/screenshot/${ssRecapNum.id}`)}
          onClose={() => setSsRecapNum(null)}
        />
      )}

      {ssShotOpen && detailNum && (
        <div className="nt-overlay" onClick={() => setSsShotOpen(false)} style={{ zIndex: 10001 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: '66vw', maxHeight: '86vh', overflow: 'auto', background: 'var(--surface)', borderRadius: 10, padding: 8, boxShadow: '0 10px 40px rgba(0,0,0,.45)' }}>
            <img src={mediaUrl(`/ssmate/screenshot/${detailNum.id}`)} alt="Schermata non disponibile"
              style={{ display: 'block', width: '100%' }} />
          </div>
        </div>
      )}

      {ssConsOpen && detailNum && (
        <LucchettoConsistenze
          lucchetto={detailNum.ssmate_lucchetto}
          codiceFiscale={detailNum.codice_fiscale}
          ragioneSociale={(detailNum.ssmate_dettaglio || {}).ragione_sociale}
          lavorabile={detailNum.ssmate_lavorabile}
          dettaglio={detailNum.ssmate_dettaglio}
          screenshotUrl={mediaUrl(`/ssmate/screenshot/${detailNum.id}`)}
          onClose={() => setSsConsOpen(false)}
        />
      )}

      {vfJobId && <VfProgress jobId={vfJobId} onDone={() => { setVfJobId(null); load(); }} />}

      {indagaOpen && detailNum && (
        <div className="nt-overlay" onClick={() => !indagaBusy && setIndagaOpen(false)} style={{ zIndex: 10002 }}>
          <div className="nt-detail" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="nt-detail-head">
              <span>Indaga — {detailNum.numero_pulito}</span>
              <button className="ob-x" onClick={() => setIndagaOpen(false)}>✕</button>
            </div>
            <div className="nt-detail-body">
              {indagaBusy && !indagaData ? (
                <IndagaAnim />
              ) : !indagaData || !indagaData.trovato ? (
                <div style={{ color: 'var(--faint)', fontSize: 13 }}>{indagaData?.errore || 'Nessun risultato su Google Maps per questo numero.'}</div>
              ) : (
                <>
                  {(() => {
                    const CONF = {
                      diretto: { t: 'Trovato per numero', c: 'var(--info)', bg: 'rgba(37,99,235,.12)' },
                      piva: { t: 'Confermato · P.IVA combacia', c: 'var(--ok)', bg: 'rgba(22,163,74,.12)' },
                      da_confermare: { t: 'Da confermare · trovato per nome', c: 'var(--warn)', bg: 'rgba(217,119,6,.12)' },
                    };
                    const cf = CONF[indagaData.conferma];
                    return cf ? (
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 11, color: cf.c, background: cf.bg }}>{cf.t}</span>
                        <span style={{ marginLeft: 8, fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>{indagaData.ragione_sociale || '—'}</span>
                      </div>
                    ) : null;
                  })()}
                  {(!indagaData.proposte || indagaData.proposte.length === 0) ? (
                    <div style={{ color: 'var(--faint)', fontSize: 13 }}>Nessuna modifica da proporre: i dati trovati sono già allineati al record.</div>
                  ) : (
                    <>
                      <div>
                        {indagaData.proposte.map((p) => {
                          const on = !!indagaSel[p.chiave];
                          return (
                            <label key={p.chiave} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid var(--surface-2)', cursor: 'pointer' }}>
                              <input type="checkbox" checked={on} onChange={(e) => setIndagaSel((s) => ({ ...s, [p.chiave]: e.target.checked }))} style={{ marginTop: 3 }} />
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', fontWeight: 700 }}>{p.etichetta}</span>
                                {p.chiave === 'social' ? (
                                  <SocialLinks social={indagaData.social} size={20} gap={8} />
                                ) : p.chiave === 'maps_url' ? (
                                  <a href={p.trovato} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--info)', fontWeight: 600 }} onClick={(e) => e.stopPropagation()}>Apri Google Maps →</a>
                                ) : (
                                  <span style={{ fontSize: 13 }}>
                                    {p.attuale ? <span style={{ color: 'var(--faint)', textDecoration: on ? 'line-through' : 'none' }}>{String(p.attuale)} </span> : <span style={{ color: 'var(--line)' }}>— </span>}
                                    <span style={{ color: 'var(--faint)' }}>→ </span>
                                    <b style={{ color: 'var(--text)' }}>{String(p.trovato)}</b>
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {indagaData.sito && <div style={{ marginTop: 8 }}><a href={indagaData.sito} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Apri sito →</a></div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                        {indagaApplied ? (
                          (indagaAgenti && (indagaAgenti.num456 || indagaAgenti.copertura)) ? (
                            <>
                              <span style={{ marginRight: 'auto', fontSize: 12.5, color: 'var(--muted)', alignSelf: 'center' }}>Dati nuovi: avviare le verifiche?</span>
                              {indagaAgenti.num456 && (
                                <button className="btn btn-primary" disabled={indagaBusy} onClick={() => { handleVerifySingle(detailNum.id, detailNum.numero_pulito); setIndagaOpen(false); }}>Avvia 456</button>
                              )}
                              {indagaAgenti.copertura && (
                                <button className="btn btn-primary" disabled={indagaBusy} onClick={() => { handleVerifyCopertura(detailNum.id); setIndagaOpen(false); }}>Avvia copertura</button>
                              )}
                              <button className="btn btn-secondary" onClick={() => setIndagaOpen(false)}>Chiudi</button>
                            </>
                          ) : (
                            <button className="btn btn-secondary" onClick={() => setIndagaOpen(false)}>Chiudi</button>
                          )
                        ) : (() => {
                          const nSel = indagaData.proposte.filter((p) => indagaSel[p.chiave]).length;
                          return (
                            <button className="btn btn-primary" disabled={indagaBusy || nSel === 0} onClick={applicaIndaga}>
                              {indagaBusy ? '…' : `Applica selezionati (${nSel})`}
                            </button>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  );
}
